import {Inject, Injectable, OnDestroy, Renderer2, RendererFactory2} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {BehaviorSubject, Observable, of as observableOf, Subject} from 'rxjs';
import {catchError, debounceTime, filter, first, map, tap} from 'rxjs/operators';
import {
  ColorConfig,
  ThemeableComponentConfig,
  ThemeableComponentName,
  ThemeableModuleConfig,
  ThemeConfig,
  ThemeMainColorType
} from './theme-config';
import {DEFAULT_THEME, PWC_THEME} from './default-theme';
import * as tinycolor from 'tinycolor2';
import {DOCUMENT} from '@angular/common';
import {FileDto} from './models/file';
import {ConfigsLoaderService} from './services/config-loader.service';
import {AuthService} from './services/auth.service';
import {MVLocalStorageService} from './services/mv-local-storage.service';
import { TenantSelectorService } from 'app/services/tenant-selector.service';
import { ToastService } from './services/toast.service';
import { TranslateService } from '@ngx-translate/core';


/**
 * Preloading Service used to avoid early initialization of unnecessary services
 */
@Injectable({ providedIn: 'root'})
export class PreloadThemingService {
  currentTheme: BehaviorSubject<ThemeConfig> = new BehaviorSubject(null);

  loadDefaultTheme() {
    return this.fetchAndApplyTheme(
      observableOf(PWC_THEME as ThemeConfig)
    );
  }

  private fetchAndApplyTheme(source: Observable<ThemeConfig>): Promise<ThemeConfig> {
    return source
      .pipe(
        // If some error happens, use some default theme
        catchError(() => observableOf(DEFAULT_THEME)),
        // This could even be a sync process,
        // but we will use a subject for this example
        tap((themeConfig) => this.currentTheme.next(themeConfig))
      )
      .toPromise();
  }

}

@Injectable({
  providedIn: 'root'
})
export class ThemingService implements OnDestroy {
  private static readonly THEME_VARIABLE_PREFIX = '--theme';
  private static readonly MODULES_THEME_VARIABLE_PREFIX = '--modules-theme';
  // Allows listening to theme changes
  private previewedTheme: BehaviorSubject<ThemeConfig>;
  // For public use
  public currentTheme: Observable<ThemeConfig>;
  // Keeps track of the persisted theme
  persistedTheme: ThemeConfig;
  private readonly renderer: Renderer2;
  // Tenant info for use only to this service
  private tenantInfo: { code: string; name: string; id: string; jsonTheme: string };

  constructor(private rendererFactory: RendererFactory2,
              private config: ConfigsLoaderService,
              private auth: AuthService,
              private preloadThemingService: PreloadThemingService,
              @Inject(DOCUMENT) private document: Document,
              private authService: AuthService,
              public _toast : ToastService,
              private translate: TranslateService,
              private selectorTenant: TenantSelectorService,
              private httpClient: HttpClient) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.previewedTheme = this.preloadThemingService.currentTheme;
    this.persistedTheme = this.previewedTheme.value;

    this.currentTheme = this.previewedTheme.asObservable();
    this.subscribeToThemeChanges();
  }

  ngOnDestroy(): void {
    this.renderer?.destroy();
  }

  /**
   * The load method must return a Promise, since that will make the application wait in the APP_INITIALIZER DI token
   * After all the loading and set up is finished, we can proceed with rendering the application
   */
  private fetchAndApplyTheme(source: Observable<ThemeConfig>): Promise<ThemeConfig> {

    return source
      .pipe(
        // If some error happens, use some default theme
        catchError(() => observableOf(DEFAULT_THEME)),
        // This could even be a sync process,
        // but we will use a subject for this example
        tap((themeConfig) => {
          this.previewedTheme.next(themeConfig);
          this.persistedTheme = themeConfig;
        })
      )
      .toPromise();
  }

  loadClientTheme(): Promise<ThemeConfig> {
    return this.fetchAndApplyTheme(
      this.httpClient.get<{ id, name, code, jsonTheme: string }>(`${this.config.apiUrl}/Tenant/`)
        .pipe(
          map(tenant => {
            // We store the tenant info for later use
            this.tenantInfo = tenant;
            return JSON.parse(tenant.jsonTheme);
          }),
          filter(x => !!x),
        )
    );
  }

  loadDefaultTheme() {
    return this.fetchAndApplyTheme(
      observableOf(PWC_THEME as ThemeConfig)
    );
  }

  private subscribeToThemeChanges(): void {
    this.previewedTheme
      .pipe(
        filter(x => !!x),
      )
      .subscribe((themeConfig: ThemeConfig) => {
        console.log('Cambio de tema');
        this.setupMainPalettes(themeConfig);
        this.setupComponentVariables(themeConfig);
        this.setupImagesVariables(themeConfig);
        this.setDarkTheme(themeConfig.darkTheme);
        this.setupModuleThemes(themeConfig.lddModules);

      });
  }

  previewTheme(theme: ThemeConfig) {
    this.previewedTheme.next(theme);
  }

  resetPreviewedTheme() {
    this.previewedTheme.next(this.persistedTheme);
  }

  persistPreviewedTheme(): void {
    
    const tenantId = this.authService.currentUser.tenantId.toString();

    const selectedTenantId = JSON.parse(MVLocalStorageService.getItem('selected_tenant'))?.id ?? tenantId;

    this.httpClient
      .post(`${this.config.apiUrl}/Tenant/UpdateTheme`,
        {
          id: selectedTenantId,
          jsonTheme: JSON.stringify(this.previewedTheme.value),
        })
      .subscribe(() => {
        this.persistedTheme = this.previewedTheme.getValue();
        this._toast.success(this.translate.instant('TOAST.SAVE_THEME'));
      }, err => {
        this._toast.error(this.translate.instant('TOAST.ERROR_SAVE_THEME'));
      });
    // localStorage.setItem('temp-theming', JSON.stringify(this.previewedTheme.value));
  }

  /**
   * This method will generate the theme palette required by Angular Material
   * It will correctly set up the variable names used in /assets/styles/_variables.scss
   * @param themeConfig
   * @private
   */
  private setupMainPalettes(themeConfig: ThemeConfig): void {
    Object.keys(themeConfig.mainColors).forEach((key: string) => {
      const selectedColorValue: string = themeConfig.mainColors[key as ThemeMainColorType];

      // Should be for example: --theme-primary or --theme-accent etc..
      const variableName: string = this.prependVariableName(this.convertCamelCaseToKebabCase(key));

      // Generate the palette colors
      const colorPalette: Array<ColorConfig> = this.generateColorPalette(selectedColorValue);

      colorPalette.forEach((colorConfig: ColorConfig) => {
        // Destructure the color config
        const {colorVariant, colorHexValue, shouldHaveDarkContrast} = colorConfig;

        // Set the color variable
        const colorVariableName = `${variableName}-${colorVariant}`;
        this.setColorVariable(colorVariableName, colorHexValue);

        // By Angular material, contrasted colors are either white, or a darker color
        // Set the contrast color
        const contrastedColorVariableName = `${variableName}-contrast-${colorVariant}`;
        const contrastedColorValue = shouldHaveDarkContrast ? 'rgba(0, 0, 0, 0.87)' : '#fff';
        this.setColorVariable(contrastedColorVariableName, contrastedColorValue);
      });
    });
  }

  /**
   * This method generates a color palette comprised of 14 main and 14 contrast colors per the Angular material specification
   * It will allow us to have different shades of some color and we can use all of those shades in our material and non-material
   * components via css.
   * The configuration can never be 100% accurate to the Material stock colors, as they are sometimes hand-made by a designer
   * So this calculation will never be 100% accurate to the original colors provided in the Material design CSS files
   * @param hexColor
   * @private
   */
  private generateColorPalette(hexColor: string): Array<ColorConfig> {
    const baseLight = tinycolor('#ffffff');
    const baseDark = this.multiply(tinycolor(hexColor).toRgb(), tinycolor(hexColor).toRgb());
    const baseTriad = tinycolor(hexColor).tetrad();

    return [
      this.mapColorConfig(tinycolor.mix(baseLight, hexColor, 12), '50'),
      this.mapColorConfig(tinycolor.mix(baseLight, hexColor, 30), '100'),
      this.mapColorConfig(tinycolor.mix(baseLight, hexColor, 50), '200'),
      this.mapColorConfig(tinycolor.mix(baseLight, hexColor, 70), '300'),
      this.mapColorConfig(tinycolor.mix(baseLight, hexColor, 85), '400'),
      this.mapColorConfig(tinycolor.mix(baseLight, hexColor, 100), '500'),
      this.mapColorConfig(tinycolor.mix(baseDark, hexColor, 87), '600'),
      this.mapColorConfig(tinycolor.mix(baseDark, hexColor, 70), '700'),
      this.mapColorConfig(tinycolor.mix(baseDark, hexColor, 54), '800'),
      this.mapColorConfig(tinycolor.mix(baseDark, hexColor, 25), '900'),
      this.mapColorConfig(tinycolor.mix(baseDark, baseTriad[3], 15).saturate(80).lighten(65), 'A100'),
      this.mapColorConfig(tinycolor.mix(baseDark, baseTriad[3], 15).saturate(80).lighten(55), 'A200'),
      this.mapColorConfig(tinycolor.mix(baseDark, baseTriad[3], 15).saturate(100).lighten(45), 'A400'),
      this.mapColorConfig(tinycolor.mix(baseDark, baseTriad[3], 15).saturate(100).lighten(40), 'A700')
    ];
  }

  /**
   * Map the color and its variant to something that we understand
   * Also check if we need to use a light or dark contrast color
   * @param tinyColorInstance
   * @param colorVariant
   * @private
   */
  private mapColorConfig(tinyColorInstance: tinycolor.Instance, colorVariant: string): ColorConfig {
    return {
      colorVariant,
      colorHexValue: tinyColorInstance.toHexString(),
      shouldHaveDarkContrast: tinyColorInstance.isLight()
    };
  }

  private multiply(rgb1: tinycolor.ColorFormats.RGB, rgb2: tinycolor.ColorFormats.RGB): tinycolor.Instance {
    rgb1.r = Math.floor((rgb1.r * rgb2.r) / 255);
    rgb1.g = Math.floor((rgb1.g * rgb2.g) / 255);
    rgb1.b = Math.floor((rgb1.b * rgb2.b) / 255);
    const {r, g, b} = rgb1;

    return tinycolor(`rgb ${r} ${g} ${b}`);
  }

  /**
   * Generate the css variables that the components will use in the theming process
   * @param theme
   * @private
   */
  private setupComponentVariables(theme: ThemeConfig): void {
    Object.keys(theme.components).forEach((componentName: string) => {
      const componentConfig: ThemeableComponentConfig = theme.components[componentName as ThemeableComponentName];
      Object.keys(componentConfig/*.colors*/).forEach((property: string) => {
        const cssVariable = this.prependVariableName(
          this.convertCamelCaseToKebabCase(`${componentName}-${property}`));
        // @ts-ignore
        const colorHex = componentConfig/*.colors*/[property];
        const colorHSL = tinycolor(colorHex).toHsl();

        this.setHSLVariables(cssVariable, colorHSL);
        this.setColorVariable(cssVariable, tinycolor(colorHex).toHslString());
      });
    });
  }

  /**
   * Generate the css variables that the images will use in the theming process
   * @param theme
   * @private
   */
  private setupImagesVariables(theme: ThemeConfig): void {
    Object.entries(theme.images).forEach(([image, source]) => {
      const cssVariable = this.prependVariableName(
        this.convertCamelCaseToKebabCase(image));
      this.setColorVariable(cssVariable, `url(${source})`);
    });
  }

  /** Generate the css variables used in the modules for the LDD FrontEnd
   *
   */
  private setupModuleThemes(lddModules: { [p: string]: ThemeableModuleConfig }) {
    Object.entries(lddModules).forEach(([moduleName, config]) => {
      // Set Text Color
      const textColor = config.textColor;

      const textColorVariableName = this.prependVariableName(
        this.convertCamelCaseToKebabCase(`${moduleName}-textColor`),
        ThemingService.MODULES_THEME_VARIABLE_PREFIX,
      );

      this.setColorVariable(textColorVariableName, tinycolor(textColor).toHslString());


      // Set Background Color
      const backgroundColor = config.backgroundColor;

      const backgroundColorVariableName = this.prependVariableName(
        this.convertCamelCaseToKebabCase(`${moduleName}-backgroundColor`),
        ThemingService.MODULES_THEME_VARIABLE_PREFIX,
      );

      this.setColorVariable(backgroundColorVariableName, tinycolor(backgroundColor).toHslString());

      // Set background Image
      if (config.backgroundImage) {
        const backgroundImage = config.backgroundImage;
        const cssVariable = this.prependVariableName(
          this.convertCamelCaseToKebabCase(`${moduleName}-backgroundImage`),
          ThemingService.MODULES_THEME_VARIABLE_PREFIX,
        );
        this.setColorVariable(cssVariable, `url(${backgroundImage})`);
      }
    });
  }

  private setHSLVariables(variableName: string, hsl: tinycolor.ColorFormats.HSLA): void {
    // we can use these variables to create lighter / darker shades by modifying the lightness value
    // can't do that with scss since the variables are generated on runtime
    this.setColorVariable(`${variableName}-h`, `${Math.round(hsl.h)}`);
    this.setColorVariable(`${variableName}-s`, `${Math.round(hsl.s * 100)}%`);
    this.setColorVariable(`${variableName}-l`, `${Math.round(hsl.l * 100)}%`);
  }

  private prependVariableName(key: string, prependName = ThemingService.THEME_VARIABLE_PREFIX): string {
    return `${prependName}-${key}`;
  }

  /**
   * Change a camelCase variable to a kebab case
   * e.g: primaryColor -> primary-color
   * @param key
   * @private
   */
  private convertCamelCaseToKebabCase(key: string): string {
    return key.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
  }

  private setColorVariable(variable: string, color: string): void {
    document.documentElement.style.setProperty(variable, color);
  }

  private setDarkTheme(isDarkTheme: boolean) {
    this.document.body.classList.remove('theme-default', 'client-default-dark', 'client-default');
    this.document.body.className = isDarkTheme ? 'client-default-dark' : 'client-default';
  }

  getLDDTheme() {
    return this.httpClient.get<{jsonTheme: string}>(`${this.config.apiUrl}/Application/GetTheme?id=${2}`)
      .pipe(
        map(res => JSON.parse(res.jsonTheme || '{}')),
      );
  }

  persistLDDTheme(theme: ThemeConfig) {
    this.httpClient.put(`${this.config.apiUrl}/Application`, {id: 2, jsonTheme: JSON.stringify(theme)})
      .subscribe(res => {
        this._toast.success(this.translate.instant(`TOAST.SAVE_GLOBAL_THEME`));
        return res;
      }, err => {
        this._toast.error(this.translate.instant(`TOAST.ERROR_SAVE_GLOBAL_THEME`));
      });
  }
}
